import {businessCalendarDate, formatBusinessTimestamp} from 'src/api/business-time'
import {parseApiDate, toApiDateString} from 'src/jobs/job-schedule-format'

import type {LoadDriver, LoadEntry, LoadEntryInput} from './schemas'

/** Per-entry load limits, matching the API. */
export const MAX_LOADS_PER_DRIVER_DAY = 50
export const MAX_LOADS_PER_DAY = 500

export type LoadEntryFormValues = {
    assignmentId: string | null
    loadDate: Date | null
    loadCount: number | null
    ticketNumber: string
}

/** Today, or the job's last day when it is in the past. */
export function defaultLoadDate(
    job: {startDate: string; endDate: string | null},
    now: Date = new Date(),
): Date {
    const today = businessCalendarDate(now)
    const lastDay = parseApiDate(job.endDate ?? job.startDate)
    return lastDay < today ? lastDay : today
}

export function emptyLoadEntryForm(
    job: {startDate: string; endDate: string | null},
    assignmentId: string | null,
    now: Date = new Date(),
): LoadEntryFormValues {
    return {assignmentId, loadDate: defaultLoadDate(job, now), loadCount: null, ticketNumber: ''}
}

/** Map an entry to form values, matching the driver by assignment id, then by name. */
export function loadEntryToForm(
    entry: Pick<
        LoadEntry,
        'assignmentId' | 'workerName' | 'loadDate' | 'loadCount' | 'ticketNumber'
    >,
    drivers: LoadDriver[] = [],
): LoadEntryFormValues {
    const listed =
        drivers.find((driver) => driver.assignmentId === entry.assignmentId) ??
        drivers.find((driver) => driver.workerName === entry.workerName)
    return {
        assignmentId:
            entry.assignmentId == null ? null : (listed?.assignmentId ?? entry.assignmentId),
        loadDate: parseApiDate(entry.loadDate),
        loadCount: entry.loadCount,
        ticketNumber: entry.ticketNumber ?? '',
    }
}

export function validateLoadEntry(
    values: LoadEntryFormValues,
    {requireDriver = false, now = new Date()}: {requireDriver?: boolean; now?: Date} = {},
): Record<string, string> {
    const errors: Record<string, string> = {}
    if (requireDriver && values.assignmentId == null) {
        errors['assignmentId'] = 'Name is required'
    }
    if (values.loadDate == null) {
        errors['loadDate'] = 'Date is required'
    } else if (values.loadDate > businessCalendarDate(now)) {
        errors['loadDate'] = 'Loads cannot be recorded for a future date'
    }
    if (values.loadCount == null || values.loadCount < 1 || !Number.isInteger(values.loadCount)) {
        errors['loadCount'] = 'Load count must be at least 1'
    } else {
        const max = values.assignmentId == null ? MAX_LOADS_PER_DAY : MAX_LOADS_PER_DRIVER_DAY
        if (values.loadCount > max) {
            errors['loadCount'] = `Load count cannot be more than ${max}`
        }
    }
    return errors
}

export function toLoadEntryInput(values: LoadEntryFormValues): LoadEntryInput {
    if (values.loadDate == null || values.loadCount == null) {
        throw new Error('Load entry is incomplete')
    }
    return {
        assignmentId: values.assignmentId,
        loadDate: toApiDateString(values.loadDate),
        loadCount: values.loadCount,
        ticketNumber: values.ticketNumber,
    }
}

export function formatLoadCount(count: number): string {
    return `${count} load${count === 1 ? '' : 's'}`
}

export function loadEntryRecordedLabel(
    entry: Pick<LoadEntry, 'enteredVia' | 'recordedByName' | 'recordedAt'>,
): string {
    const when = formatBusinessTimestamp(entry.recordedAt, 'MMM d, h:mm a')
    if (entry.enteredVia === 'driver_link') {
        return `From the driver link · ${when}`
    }

    return entry.recordedByName ? `Recorded by ${entry.recordedByName} · ${when}` : when
}

export function loadEntryVoidedLabel(
    entry: Pick<LoadEntry, 'voidedAt' | 'voidedByName' | 'voidReason'>,
): string | null {
    if (entry.voidedAt == null) {
        return null
    }

    const by = entry.voidedByName ? ` by ${entry.voidedByName}` : ''
    const reason = entry.voidReason ? `: ${entry.voidReason}` : ''
    return `Voided${by} · ${formatBusinessTimestamp(entry.voidedAt, 'MMM d, h:mm a')}${reason}`
}
